import React, { useEffect, useRef, useState } from 'react';
import { usePortContext } from '../../../service/MessageChannelContext';
import { Spinner } from 'reactstrap';

import { useNavigate } from 'react-router-dom';
import SelfieButton from '../../../images/selfie-button.png';
import { Navbar } from '../NavBar/NavBar';
import './takeSelfie.scss';
import { Button } from '../Button/Button';

import { detectorPromise } from '../../../common/utils';

const FACE_OVAL_ANNOTATIONS = [
  10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379, 378, 400, 377, 152, 148, 176, 149, 150, 136, 172, 58, 132, 93,
  234, 127, 162, 21, 54, 103, 67, 109, 10,
];

const TZONE_ANNOTATIONS = [297, 293, 334, 296, 285, 417, 399, 420, 360, 1, 131, 198, 174, 193, 55, 66, 105, 63, 67, 297];
function isOutsideEllipse(x, y, a, b, h, k) {
  let value = Math.pow(x - h, 2) / (a * a) + Math.pow(y - k, 2) / (b * b);
  return value > 1;
}

function calculateEllipseOnSource(clientWidth, clientHeight, videoWidth, videoHeight) {
  const videoAspectRatio = videoWidth / videoHeight;
  const clientAspectRatio = clientWidth / clientHeight;

  let croppedWidth, croppedHeight, cropFromEachSide, cropFromTop;

  if (clientAspectRatio > videoAspectRatio) {
    const scale = clientWidth / videoWidth;
    croppedWidth = videoWidth;
    croppedHeight = clientHeight / scale;
    cropFromTop = (videoHeight - croppedHeight) / 2;
    cropFromEachSide = 0;
  } else {
    const scale = clientHeight / videoHeight;
    croppedHeight = videoHeight;
    croppedWidth = clientWidth / scale;
    cropFromEachSide = (videoWidth - croppedWidth) / 2;
    cropFromTop = 0;
  }

  const xCroppedCenter = 0.5 * croppedWidth;
  const yCroppedCenter = 0.5 * croppedHeight;

  const xOriginalCenter = xCroppedCenter + cropFromEachSide;
  const yOriginalCenter = yCroppedCenter + cropFromTop;

  return {
    xCenter: xOriginalCenter,
    yCenter: yOriginalCenter,
    widthRadius: xCroppedCenter * 0.7,
    heightRadius: yCroppedCenter * 0.7,
  };
}

function getDisplayMessage(isNear, isFaceOutside, isFacingCamera) {
  if (isFaceOutside) {
    return 'Keep your face inside the circle';
  } else if (!isNear) {
    return 'Move your face closer to the camera';
  } else if (!isFacingCamera) {
    return 'Face the camera';
  } else {
    return 'Take a selfie';
  }
}

async function getMediaStream() {
  const constraints = {
    video: {
      facingMode: 'user',
      width: { ideal: 4096 },
      height: { ideal: 2160 },
    },
  };

  const fallbackConstraints = {
    video: {
      facingMode: 'user',
      width: { ideal: 1920 },
      height: { ideal: 1080 },
    },
  };

  const secondFallbackConstraints = {
    video: {
      facingMode: 'user',
      width: { ideal: 1280 },
      height: { ideal: 720 },
    },
  };

  const tertiaryFallbackConstraints = {
    video: {
      facingMode: 'user',
      width: { ideal: 640 },
      height: { ideal: 480 },
    },
  };

  return await navigator.mediaDevices
    .getUserMedia(constraints)
    .catch(() => navigator.mediaDevices.getUserMedia(fallbackConstraints))
    .catch(() => navigator.mediaDevices.getUserMedia(secondFallbackConstraints))
    .catch(() => navigator.mediaDevices.getUserMedia(tertiaryFallbackConstraints));
}

// function getCorrectPaddingWeight(box, videoWidth, videoHeight) {
//   for (let paddingWeight of [0.7, 0.65, 0.6, 0.55, 0.5, 0.45, 0.4, 0.35, 0.3, 0.25, 0.2, 0.15, 0.1, 0.05]) {
//     const paddedXMin = box.xMin - paddingWeight * box.width;
//     const paddedYMin = box.yMin - paddingWeight * box.height;
//     const paddedXMax = box.xMax + paddingWeight * box.width;
//     const paddedYMax = box.yMax + paddingWeight * box.height;

//     if (paddedXMin >= 0 && paddedYMin >= 0 && paddedXMax <= videoWidth && paddedYMax <= videoHeight) {
//       return paddingWeight;
//     }
//   }
//   return 0;
// }

// function createTransformer(srcX, srcY, srcWidth, srcHeight, destWidth, destHeight) {
//   return function ([x, y]) {
//     return [((x - srcX) / srcWidth) * destWidth, ((y - srcY) / srcHeight) * destHeight];
//   };
// }

const TakeSelfieCam = () => {
  const videoRef = useRef(null);
  const animationCanvasRef = useRef(null);

  const [mediaStream, setMediaStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [isFaceOutside, setIsFaceOutside] = useState(true);
  const [isNear, setIsNear] = useState(false);
  const [isFacingCamera, setIsFacingCamera] = useState(false);
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [imagePoints, setImagePoints] = useState([]);
  const [intervalId, setIntervalId] = useState(null);

  const [animationImage, setAnimationImage] = useState(null);

  const {
    setKeypointsData,
    setBoundingBoxData,
    setImage,
    events: { emitUploadPhotoEvent },
  } = usePortContext();

  const detectFaces = async () => {
    if (!videoRef.current) return;
    const faceLandmarker = await detectorPromise;
    faceLandmarker.setOptions({ runningMode: 'VIDEO' });

    let predictions;
    try {
      predictions = faceLandmarker.detectForVideo(videoRef.current, Date.now());
    } catch (error) {
      console.error('Error detecting faces', error);
      return;
    }

    // const detector = await detectorPromise;
    // const predictions = await detector.estimateFaces(videoRef.current);

    if (!predictions.faceLandmarks.length) {
      setIsFaceOutside(true);
      return;
    }

    const { clientWidth, clientHeight, videoWidth, videoHeight } = videoRef.current;
    const keypoints = predictions.faceLandmarks[0].map(({ x, y, z }) => ({ x: x * videoWidth, y: y * videoHeight, z: z * videoWidth }));

    const xCoords = keypoints.map(({ x }) => x);
    const yCoords = keypoints.map(({ y }) => y);
    const xMin = Math.min(...xCoords);
    const xMax = Math.max(...xCoords);
    const yMin = Math.min(...yCoords);
    const yMax = Math.max(...yCoords);

    const scaledMesh = keypoints.map(({ x, y }) => [x, y]);
    const boundingBox = {
      xMin,
      xMax,
      yMin,
      yMax,
      width: xMax - xMin,
      height: yMax - yMin,
    };

    if (!scaledMesh?.length) {
      setIsFaceOutside(true);
      return;
    }

    const faceOval = FACE_OVAL_ANNOTATIONS.map((i) => keypoints[i]);
    const tZone = TZONE_ANNOTATIONS.map((i) => keypoints[i]);
    const faceOvalReversed = [...faceOval].reverse();
    const tZoneReversed = [...tZone].reverse();

    const faceOvalLeft = faceOval.slice(0, Math.floor(faceOval.length / 2));
    const faceOvalRight = faceOvalReversed.slice(0, Math.floor(faceOvalReversed.length / 2));

    const tZoneLeft = tZone.slice(0, Math.floor(tZone.length / 2));
    const tZoneRight = tZoneReversed.slice(0, Math.floor(tZoneReversed.length / 2));

    const {
      xCenter: centerPointX,
      yCenter: centerPointY,
      widthRadius: radiusWidth,
      heightRadius: radiusHeight,
    } = calculateEllipseOnSource(clientWidth, clientHeight, videoWidth, videoHeight);

    const isOutside = !!faceOval?.filter(({ x, y }) => isOutsideEllipse(x, y, radiusWidth, radiusHeight, centerPointX, centerPointY))
      .length;

    const diffThreshold = 150;
    const faceOvalDifferences = faceOvalLeft.map(({ z }, i) => z - faceOvalRight[i].z).filter((z) => Math.abs(z) >= diffThreshold);
    const tZoneDifferences = tZoneLeft.map(({ z }, i) => z - tZoneRight[i].z).filter((z) => Math.abs(z) >= diffThreshold);

    const isNear = radiusHeight * 1.4 <= boundingBox?.height || radiusWidth * 1.4 <= boundingBox?.width;
    const isFacing = !faceOvalDifferences.length && !tZoneDifferences.length;
    setIsFaceOutside(isOutside);
    setIsNear(isNear);
    setIsFacingCamera(isFacing);
  };

  const takeSelfie = async () => {
    if (isFaceOutside || !isNear || !isFacingCamera) {
      return;
    }
    const videoElement = videoRef.current;
    videoElement.pause();
    setIsLoading(true);
    clearInterval(intervalId);
    const detector = await detectorPromise;
    setIsLoading(false);

    const animationCanvas = document.createElement('canvas');
    /**
     * @type {CanvasRenderingContext2D}
     */
    const animationContext = animationCanvas.getContext('2d');
    animationCanvas.width = Math.min(window.innerWidth, 0.7 * window.innerHeight);
    animationCanvas.height = window.innerHeight;

    const animationCanvasAspectRatio = animationCanvas.width / animationCanvas.height;
    const videoAspectRatio = videoElement.videoWidth / videoElement.videoHeight;

    let sourceX, sourceY, sourceWidth, sourceHeight;

    if (animationCanvasAspectRatio > videoAspectRatio) {
      const scale = animationCanvas.width / videoElement.videoWidth;
      sourceWidth = videoElement.videoWidth;
      sourceHeight = animationCanvas.height / scale;
      sourceX = 0;
      sourceY = Math.abs((sourceHeight - videoElement.videoHeight) / 2);
    } else {
      const scale = animationCanvas.height / videoElement.videoHeight;
      sourceHeight = videoElement.videoHeight;
      sourceWidth = animationCanvas.width / scale;
      sourceY = 0;
      sourceX = Math.abs((sourceWidth - videoElement.videoWidth) / 2);
    }

    // animationContext.save();
    animationContext.scale(-1, 1);

    animationContext.drawImage(
      videoElement,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      -animationCanvas.width,
      0,
      animationCanvas.width,
      animationCanvas.height,
    );

    // animationContext.restore();

    const animationBlob = await new Promise((resolve) => animationCanvas.toBlob(resolve, 'image/jpeg', 0.95));
    const animationFile = new File([animationBlob], 'animation_image.jpg', { type: 'image/jpeg' });

    detector.setOptions({ runningMode: 'IMAGE' });
    const animationPrediction = detector.detect(animationCanvas);

    const animationKeypoints = animationPrediction.faceLandmarks[0].map(({ x, y, z }) => ({
      x: x * animationCanvas.width,
      y: y * animationCanvas.height,
      z: z * animationCanvas.width,
    }));

    const xCoords = animationKeypoints.map(({ x }) => x);
    const yCoords = animationKeypoints.map(({ y }) => y);

    const xMin = Math.min(...xCoords);
    const xMax = Math.max(...xCoords);
    const yMin = Math.min(...yCoords);
    const yMax = Math.max(...yCoords);

    const boundingBox = {
      xMin,
      xMax,
      yMin,
      yMax,
      width: xMax - xMin,
      height: yMax - yMin,
    };
    setKeypointsData(animationKeypoints);
    setBoundingBoxData(boundingBox);
    setAnimationImage(animationFile);
    setCapturedImage(animationFile);
    setImagePoints(animationKeypoints.map(({ x, y }) => [x, y]));
  };

  useEffect(() => {
    return () => {
      if (mediaStream) {
        mediaStream?.getTracks()?.forEach((track) => track.stop());
      }
    };
  }, [mediaStream]);

  useEffect(() => {
    return () => {
      if (mediaStream) {
        mediaStream?.getTracks()?.forEach((track) => track.stop());
      }
    };
  }, []);

  useEffect(() => {
    getMediaStream().then((stream) => setMediaStream(stream));
    return () => {
      if (mediaStream) {
        mediaStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [videoRef.current]);

  useEffect(() => {
    if (!mediaStream) return;

    if (videoRef.current) {
      videoRef.current.srcObject = mediaStream;
    }

    const interval = setInterval(() => {
      detectFaces();
    }, 500);
    if (intervalId) {
      clearInterval(intervalId);
    }
    setIntervalId(interval);

    return () => clearInterval(intervalId);
  }, [mediaStream]);

  const imageSubmission = async () => {
    if (!capturedImage) return;

    emitUploadPhotoEvent(capturedImage, imagePoints);
    await createImageBitmap(animationImage).then(setImage);
    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target.result;
      navigate('/reportAnalysis', { state: dataUrl });
    };
    reader.readAsDataURL(capturedImage);
  };

  return (
    <div className="camera-container">
      <Navbar pinned leftButtonAction={capturedImage ? () => setCapturedImage(null) : undefined} />
      {!capturedImage && mediaStream && (
        <div className="takepicture">
          <div className="video-wrapper">
            <video ref={videoRef} autoPlay muted playsInline className="camera-video" />
            <div className={!isFaceOutside && isNear && isFacingCamera ? 'overlay success' : 'overlay'}></div>
            <div className="capture-button-box">
              <h6>{getDisplayMessage(isNear, isFaceOutside, isFacingCamera)}</h6>
              <img src={SelfieButton} alt="Selfie Button" className="capture-button" onClick={takeSelfie} />
            </div>
          </div>
        </div>
      )}

      {capturedImage && (
        <div className="savepictureArea">
          <img src={URL.createObjectURL(capturedImage)} alt="Captured" className="captured-image" />
          <div className="selectImageArea">
            <Button
              text={'Submit'}
              onClick={() => {
                imageSubmission();
                mediaStream?.getTracks()?.forEach((track) => track.stop());
              }}
            />
            <div
              className="retake-button"
              onClick={() => {
                setCapturedImage(null);
              }}>
              Retake
            </div>
          </div>
        </div>
      )}

      {isLoading && (
        <div className={`loader-overlay ${isLoading ? 'fade-in' : ''}`}>
          <Spinner color="primary" />
        </div>
      )}
    </div>
  );
};

export default TakeSelfieCam;
